import { Stack, Text, useTheme, Button } from '@ltvco/refresh-lib/theme';
import { convertImageToBase64 } from '@ltvco/refresh-lib/utils';
import { AutoAwesomeOutlined, ArrowUpwardOutlined } from '@mui/icons-material';
import { useRef } from 'react';

const FOUR_MEGABYTES = 4194304;

type UploadPhotoInstructionProps = {
  setImageToAnalyze: (image: string) => void;
  onSendMessage?: (message?: string, imageBase64?: string) => void;
  hasImageToAnalyze: boolean;
  onImageErrorChange: (hasError: boolean) => void;
};

export const UploadPhotoInstruction = ({
  setImageToAnalyze,
  onSendMessage,
  hasImageToAnalyze = false,
  onImageErrorChange,
}: UploadPhotoInstructionProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // Clear any previous image-related error when user selects a new file
    onImageErrorChange(false);

    const file = event.target.files?.[0] ?? null;

    if (!file || !file.type.includes('image')) {
      onSendMessage?.(
        'The selected file is not an image. Please upload an image file.',
      );
      event.target.value = '';
      return;
    }

    if (file.size > FOUR_MEGABYTES) {
      onImageErrorChange(true);
      onSendMessage?.(
        'The selected image is larger than 4MB. Please upload a smaller file.',
      );
      event.target.value = '';
      return;
    }

    try {
      const imageBase64 = await convertImageToBase64(file);
      const base64StringWithoutPrefix = imageBase64.replace(
        /^data:image\/\w+;base64,/,
        '',
      );
      setImageToAnalyze(base64StringWithoutPrefix);
      onImageErrorChange(false);
      onSendMessage?.(undefined, base64StringWithoutPrefix);
    } catch {
      onSendMessage?.(
        'Something went wrong while processing the image. Please try again.',
      );
    } finally {
      event.target.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Stack width="100%" alignItems="center">
      <Stack width="100%" maxWidth="760px" spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          p={2}
          sx={{ backgroundColor: 'background.default', borderRadius: 2 }}
        >
          <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
          <Text variant="body1">
            Hi! I&apos;m your AI vehicle research assistant. I&apos;d be happy
            to help analyze that damage! Please upload a photo of the damage.
          </Text>
        </Stack>
        {!hasImageToAnalyze && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            p={2}
            sx={{ backgroundColor: 'background.default', borderRadius: 2 }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
              <Text variant="body1">Upload a photo of the damage</Text>
            </Stack>
            <Button variant="outlined" onClick={handleButtonClick}>
              <ArrowUpwardOutlined
                sx={{
                  color: theme.palette.button.light,
                  fontSize: '1.25rem',
                  mb: 0.5,
                }}
              />
              <Text variant="body2" ml={0.5}>
                Upload
              </Text>
            </Button>
            <input
              ref={fileInputRef}
              accept="image/jpeg, image/jpg, image/png, image/webp"
              onChange={handleFileChange}
              id="contained-button-file"
              multiple={false}
              type="file"
              hidden
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
